// Every GraphQL document this client sends, in one file.
//
// `edu-app/docs/SCHEMA.graphql` is a FROZEN CONTRACT, written before any
// implementation; the client, the BFF resolvers and the BFF's own tests are
// built against it in parallel. A field that is not in it does not exist.
// Collecting the documents here makes the app's whole demand on the BFF one page
// long, and lets `tests/documents.test.js` check every selection, argument and
// variable type against the contract.
//
// ── The conventions the schema is explicit about (core.md §10) ────────────
//
// 1. EPOCH MILLISECONDS RIDE AS `String`, NEVER `Int` — GraphQL's Int is 32-bit
//    and epoch ms overflow it; Apollo THROWS at serialization. Every `*AtMs` is a
//    String and is converted with `Number()` at the point of use.
// 2. NOTHING TAKES A `studentId` ARGUMENT. The student id is the JWT `sub`; an
//    argument that exists is an argument someone eventually passes.
// 3. Lists that omit children populate the COUNTS: `entries` is empty on
//    `myCases`, `messages` is empty on `caseChatThreads`, `note`/`diagnoses`/…
//    are empty on `encounters`. Render the count, never `.length`.
// 4. Reads answer `null` for "not found" and "not mine" alike — one screen for
//    both, no oracle.
//
// The encounter / clinical / copilot documents are declared here too, ahead of
// the Visit workspace that will send them, so the contract check covers them
// from day one.

// ── Fragments ───────────────────────────────────────────────────────────────

/** The patient as a LIST ROW: counts, no children. */
export const CASE_ROW_FRAGMENT = `
  fragment CaseRow on StudentCase {
    id
    title
    displayName
    caseRef
    ageYears
    ageDisplay
    sex
    bloodType
    weightKg
    heightCm
    chiefComplaint
    status
    note
    entryCount
    threadCount
    messageCount
    encounterCount
    lastEncounterAtMs
    openEncounterId
    createdAtMs
    updatedAtMs
  }
`;

export const HISTORY_ENTRY_FRAGMENT = `
  fragment HistoryEntry on CaseHistoryEntry {
    id
    caseId
    section
    label
    body
    structured
    source
    sortOrder
    recordedAtMs
    encounterId
    createdAtMs
    updatedAtMs
  }
`;

export const CHAT_MESSAGE_FRAGMENT = `
  fragment ChatMessage on CaseChatMessage {
    id
    threadId
    role
    kind
    content
    payload
    costParseh
    seq
    createdAtMs
  }
`;

/** A thread WITHOUT its transcript — what a sidebar row renders. */
export const CHAT_THREAD_ROW_FRAGMENT = `
  fragment ChatThreadRow on CaseChatThread {
    id
    caseId
    encounterId
    title
    messageCount
    lastMessageAtMs
    createdAtMs
    hasOlder
  }
`;

/** A thread WITH its transcript, oldest first. */
export const CHAT_THREAD_FULL_FRAGMENT = `
  fragment ChatThreadFull on CaseChatThread {
    ...ChatThreadRow
    messages { ...ChatMessage }
  }
`;

export const ENCOUNTER_NOTE_FRAGMENT = `
  fragment EncounterNote on EncounterNote {
    encounterId
    s
    o
    a
    p
    updatedAtMs
  }
`;

export const DIAGNOSIS_FRAGMENT = `
  fragment Diagnosis on Diagnosis {
    id
    caseId
    encounterId
    title
    icdCode
    kind
    status
    note
    origin
    sortOrder
    createdAtMs
    updatedAtMs
  }
`;

export const RX_ITEM_FRAGMENT = `
  fragment RxItem on RxItem {
    id
    caseId
    encounterId
    drugId
    drugName
    dose
    route
    frequency
    duration
    quantity
    instructions
    origin
    sortOrder
    createdAtMs
    updatedAtMs
  }
`;

export const ORDER_FRAGMENT = `
  fragment Order on Order {
    id
    caseId
    encounterId
    orderType
    title
    code
    category
    urgency
    status
    notes
    resultBody
    resultStructured
    resultedAtMs
    origin
    sortOrder
    createdAtMs
    updatedAtMs
  }
`;

/** An encounter as a LIST ROW: counts, no children. */
export const ENCOUNTER_ROW_FRAGMENT = `
  fragment EncounterRow on Encounter {
    id
    caseId
    seq
    status
    reason
    startedAtMs
    endedAtMs
    summary
    diagnosisCount
    rxCount
    orderCount
    entryCount
    threadCount
    createdAtMs
    updatedAtMs
  }
`;

/** An encounter WITH everything the workspace edits. */
export const ENCOUNTER_FULL_FRAGMENT = `
  fragment EncounterFull on Encounter {
    ...EncounterRow
    note { ...EncounterNote }
    diagnoses { ...Diagnosis }
    rxItems { ...RxItem }
    orders { ...Order }
    entries { ...HistoryEntry }
  }
`;

export const WALLET_BALANCE_FRAGMENT = `
  fragment WalletBalance on StudentWalletBalance {
    balance
    currency
    lastUpdated
  }
`;

export const GIFT_CODE_FRAGMENT = `
  fragment GiftCode on StudentGiftCode {
    id
    code
    parseh
    status
    issuedAtMs
    expiresAtMs
    redeemedAtMs
  }
`;

export const STUDENT_PROFILE_FRAGMENT = `
  fragment StudentProfile on StudentProfile {
    id
    fullName
    studentNumber
    university
    level
    field
    entryYear
    bio
    phone
    caseCount
    balance
    createdAtMs
    updatedAtMs
  }
`;

// ── Queries: identity ───────────────────────────────────────────────────────

/**
 * The boot query: is this person onboarded, and who are they.
 *
 * Only the two roots the gate ALWAYS allows (core.md §4.2). `myWalletBalance` is
 * deliberately absent: it is gated on the profile, and a field-level
 * NOT_ONBOARDED beside a perfectly good `onboardingStatus` would make the whole
 * boot look like an error. The balance rides on `studentProfile.balance`, which
 * the contract says is the same number.
 */
export const BOOT_QUERY = `
  query Boot {
    onboardingStatus {
      isOnboarded
      suggestedFullName
      phone
    }
    studentProfile { ...StudentProfile }
  }
  ${STUDENT_PROFILE_FRAGMENT}
`;

export const STUDENT_PROFILE_QUERY = `
  query StudentProfile {
    studentProfile { ...StudentProfile }
  }
  ${STUDENT_PROFILE_FRAGMENT}
`;

// ── Queries: the patients ──────────────────────────────────────────────────

/**
 * This student's practice patients. No search argument by design — a server-side
 * text search is the one query shape that becomes cross-student by dropping a
 * single predicate. The list is capped at 100 server-side; the UI filters, sorts
 * and pages what it already holds.
 */
export const MY_CASES_QUERY = `
  query MyCases($statuses: [String!], $limit: Int, $offset: Int) {
    myCases(statuses: $statuses, limit: $limit, offset: $offset) {
      ...CaseRow
    }
  }
  ${CASE_ROW_FRAGMENT}
`;

/**
 * "Does this student have ANY patient?" — every status, one row, ids only.
 *
 * The list query carries no `total` and takes `statuses` as a wire filter, so
 * an empty filtered page cannot tell «هنوز بیماری نساخته‌اید» from «موردی
 * مطابق فیلتر نیست». This is the cheapest read the contract allows that can:
 * `limit: 1`, no status filter, one field. Asked once per session (hooks/
 * useCases.js caches the answer) and never paged.
 */
export const ANY_CASES_QUERY = `
  query AnyCases($limit: Int, $offset: Int) {
    myCases(limit: $limit, offset: $offset) {
      id
    }
  }
`;

/** One patient WITH its history. Null for not-found and not-mine alike. */
export const MY_CASE_QUERY = `
  query MyCase($caseId: ID!) {
    myCase(caseId: $caseId) {
      ...CaseRow
      entries { ...HistoryEntry }
    }
  }
  ${CASE_ROW_FRAGMENT}
  ${HISTORY_ENTRY_FRAGMENT}
`;

/**
 * The whole record in one round trip: the patient with entries, every encounter
 * with its note/dx/rx/orders/entries, thread summaries, and the BFF-derived
 * views (active problems, current medications, pending orders, timeline). The
 * deepest legitimate query; it fits the depth limit at 6.
 */
export const MY_CASE_RECORD_QUERY = `
  query MyCaseRecord($caseId: ID!) {
    myCaseRecord(caseId: $caseId) {
      case {
        ...CaseRow
        entries { ...HistoryEntry }
      }
      encounters { ...EncounterFull }
      threads { ...ChatThreadRow }
      activeProblems {
        title
        icdCode
        kind
        encounterId
        encounterSeq
        sinceMs
      }
      currentMedications {
        name
        detail
        from
        encounterSeq
      }
      pendingOrders { ...Order }
      timeline {
        id
        type
        atMs
        title
        detail
        encounterId
        encounterSeq
        refId
      }
    }
  }
  ${CASE_ROW_FRAGMENT}
  ${HISTORY_ENTRY_FRAGMENT}
  ${ENCOUNTER_ROW_FRAGMENT}
  ${ENCOUNTER_NOTE_FRAGMENT}
  ${DIAGNOSIS_FRAGMENT}
  ${RX_ITEM_FRAGMENT}
  ${ORDER_FRAGMENT}
  ${ENCOUNTER_FULL_FRAGMENT}
  ${CHAT_THREAD_ROW_FRAGMENT}
`;

/** The history alone — the editor re-reads after a save without repainting the header. */
export const CASE_HISTORY_QUERY = `
  query CaseHistory($caseId: ID!, $sections: [String!]) {
    caseHistory(caseId: $caseId, sections: $sections) {
      ...HistoryEntry
    }
  }
  ${HISTORY_ENTRY_FRAGMENT}
`;

// ── Queries: encounters ────────────────────────────────────────────────────

export const ENCOUNTER_QUERY = `
  query Encounter($encounterId: ID!) {
    encounter(encounterId: $encounterId) { ...EncounterFull }
  }
  ${ENCOUNTER_ROW_FRAGMENT}
  ${ENCOUNTER_NOTE_FRAGMENT}
  ${DIAGNOSIS_FRAGMENT}
  ${RX_ITEM_FRAGMENT}
  ${ORDER_FRAGMENT}
  ${HISTORY_ENTRY_FRAGMENT}
  ${ENCOUNTER_FULL_FRAGMENT}
`;

/** The visits of one patient, newest first — rows with counts, no children. */
export const ENCOUNTERS_QUERY = `
  query Encounters($caseId: ID!) {
    encounters(caseId: $caseId) { ...EncounterRow }
  }
  ${ENCOUNTER_ROW_FRAGMENT}
`;

// ── Queries: chat ──────────────────────────────────────────────────────────

/** `scope`: PATIENT (default) | ENCOUNTER (needs encounterId) | ALL. Messages EMPTY. */
export const CASE_CHAT_THREADS_QUERY = `
  query CaseChatThreads($caseId: ID!, $scope: String, $encounterId: ID) {
    caseChatThreads(caseId: $caseId, scope: $scope, encounterId: $encounterId) {
      ...ChatThreadRow
    }
  }
  ${CHAT_THREAD_ROW_FRAGMENT}
`;

/**
 * One thread WITH its messages, oldest first. `messageLimit` is the newest N;
 * `beforeSeq` pages backwards from a message's `seq` («پیام‌های قدیمی‌تر» is a
 * button, never a scroll trigger).
 */
export const CASE_CHAT_THREAD_QUERY = `
  query CaseChatThread($threadId: ID!, $messageLimit: Int, $beforeSeq: String) {
    caseChatThread(threadId: $threadId, messageLimit: $messageLimit, beforeSeq: $beforeSeq) {
      ...ChatThreadFull
    }
  }
  ${CHAT_THREAD_ROW_FRAGMENT}
  ${CHAT_MESSAGE_FRAGMENT}
  ${CHAT_THREAD_FULL_FRAGMENT}
`;

// ── Queries: the formulary and the catalog (free) ─────────────────────────

export const SEARCH_DRUGS_QUERY = `
  query SearchDrugs($query: String!, $limit: Int) {
    searchDrugs(query: $query, limit: $limit) {
      id
      name
      genericName
      category
      commonDosages
      commonFrequencies
    }
  }
`;

export const ORDER_CATALOG_QUERY = `
  query OrderCatalog {
    orderCatalog {
      labs { orderType name nameEn code category modality }
      imaging { orderType name nameEn code category modality }
      procedures { orderType name nameEn code category modality }
    }
  }
`;

// ── Queries: the wallet ────────────────────────────────────────────────────

/** Reading this IS the starter grant (500 پارسه, monthly). There is no grant mutation. */
export const WALLET_BALANCE_QUERY = `
  query MyWalletBalance {
    myWalletBalance { ...WalletBalance }
  }
  ${WALLET_BALANCE_FRAGMENT}
`;

export const WALLET_TRANSACTIONS_QUERY = `
  query MyWalletTransactions($limit: Int, $offset: Int, $type: String) {
    myWalletTransactions(limit: $limit, offset: $offset, type: $type) {
      transactions {
        id
        type
        amount
        serviceType
        description
        createdAt
        referenceId
      }
      totalCount
    }
  }
`;

export const MY_GIFT_CODES_QUERY = `
  query MyGiftCodes {
    myGiftCodes { ...GiftCode }
  }
  ${GIFT_CODE_FRAGMENT}
`;

export const GIFT_CODE_PREVIEW_QUERY = `
  query GiftCodePreview($code: String!) {
    giftCodePreview(code: $code) { ...GiftCode }
  }
  ${GIFT_CODE_FRAGMENT}
`;

// ── Mutations: identity ────────────────────────────────────────────────────

export const COMPLETE_ONBOARDING_MUTATION = `
  mutation CompleteStudentOnboarding($input: StudentOnboardingInput!) {
    completeStudentOnboarding(input: $input) { ...StudentProfile }
  }
  ${STUDENT_PROFILE_FRAGMENT}
`;

/** Every field optional; omitted = untouched; "" clears (0 for entryYear). */
export const UPDATE_PROFILE_MUTATION = `
  mutation UpdateStudentProfile($input: StudentProfileUpdateInput!) {
    updateStudentProfile(input: $input) { ...StudentProfile }
  }
  ${STUDENT_PROFILE_FRAGMENT}
`;

// ── Mutations: the patient ─────────────────────────────────────────────────

export const CREATE_CASE_MUTATION = `
  mutation CreateCase($input: CaseInput!) {
    createCase(input: $input) { ...CaseRow }
  }
  ${CASE_ROW_FRAGMENT}
`;

/** Omitted fields are LEFT ALONE; send `""` (or `0` for a number) to clear one. */
export const UPDATE_CASE_MUTATION = `
  mutation UpdateCase($caseId: ID!, $input: CaseUpdateInput!) {
    updateCase(caseId: $caseId, input: $input) { ...CaseRow }
  }
  ${CASE_ROW_FRAGMENT}
`;

/** Deletes the patient AND every child row in one transaction. No undo, no refund. */
export const DELETE_CASE_MUTATION = `
  mutation DeleteCase($caseId: ID!) {
    deleteCase(caseId: $caseId)
  }
`;

// ── Mutations: the history ─────────────────────────────────────────────────

/** Append entries. ALL-OR-NOTHING; `encounterId` stamps an OPEN visit. */
export const ADD_CASE_HISTORY_MUTATION = `
  mutation AddCaseHistory($caseId: ID!, $entries: [CaseHistoryEntryInput!]!) {
    addCaseHistory(caseId: $caseId, entries: $entries) { ...HistoryEntry }
  }
  ${HISTORY_ENTRY_FRAGMENT}
`;

/** `section` and `source` are NOT in the update input — provenance is a fact. */
export const UPDATE_HISTORY_ENTRY_MUTATION = `
  mutation UpdateCaseHistoryEntry($entryId: ID!, $input: CaseHistoryEntryUpdateInput!) {
    updateCaseHistoryEntry(entryId: $entryId, input: $input) { ...HistoryEntry }
  }
  ${HISTORY_ENTRY_FRAGMENT}
`;

export const DELETE_HISTORY_ENTRY_MUTATION = `
  mutation DeleteCaseHistoryEntry($entryId: ID!) {
    deleteCaseHistoryEntry(entryId: $entryId)
  }
`;

// ── Mutations: the encounter lifecycle (core.md §6) ────────────────────────

/** One open visit per patient: ENCOUNTER_ALREADY_OPEN carries `extensions.encounterId`. */
export const START_ENCOUNTER_MUTATION = `
  mutation StartEncounter($caseId: ID!, $reason: String) {
    startEncounter(caseId: $caseId, reason: $reason) { ...EncounterRow }
  }
  ${ENCOUNTER_ROW_FRAGMENT}
`;

/** Closing needs nothing; `summary` is optional. */
export const END_ENCOUNTER_MUTATION = `
  mutation EndEncounter($encounterId: ID!, $summary: String) {
    endEncounter(encounterId: $encounterId, summary: $summary) { ...EncounterRow }
  }
  ${ENCOUNTER_ROW_FRAGMENT}
`;

export const REOPEN_ENCOUNTER_MUTATION = `
  mutation ReopenEncounter($encounterId: ID!) {
    reopenEncounter(encounterId: $encounterId) { ...EncounterRow }
  }
  ${ENCOUNTER_ROW_FRAGMENT}
`;

/** Hard delete of the visit and its children; stamped history entries survive. */
export const CANCEL_ENCOUNTER_MUTATION = `
  mutation CancelEncounter($encounterId: ID!) {
    cancelEncounter(encounterId: $encounterId)
  }
`;

/** The SOAP note — one shape. Omitted = untouched, "" = cleared. */
export const UPSERT_ENCOUNTER_NOTE_MUTATION = `
  mutation UpsertEncounterNote($encounterId: ID!, $input: EncounterNoteInput!) {
    upsertEncounterNote(encounterId: $encounterId, input: $input) { ...EncounterNote }
  }
  ${ENCOUNTER_NOTE_FRAGMENT}
`;

// ── Mutations: clinical rows ───────────────────────────────────────────────

export const ADD_DIAGNOSIS_MUTATION = `
  mutation AddDiagnosis($encounterId: ID!, $input: DiagnosisInput!) {
    addDiagnosis(encounterId: $encounterId, input: $input) { ...Diagnosis }
  }
  ${DIAGNOSIS_FRAGMENT}
`;

export const UPDATE_DIAGNOSIS_MUTATION = `
  mutation UpdateDiagnosis($diagnosisId: ID!, $input: DiagnosisUpdateInput!) {
    updateDiagnosis(diagnosisId: $diagnosisId, input: $input) { ...Diagnosis }
  }
  ${DIAGNOSIS_FRAGMENT}
`;

export const DELETE_DIAGNOSIS_MUTATION = `
  mutation DeleteDiagnosis($diagnosisId: ID!) {
    deleteDiagnosis(diagnosisId: $diagnosisId)
  }
`;

export const ADD_RX_ITEM_MUTATION = `
  mutation AddRxItem($encounterId: ID!, $input: RxItemInput!) {
    addRxItem(encounterId: $encounterId, input: $input) { ...RxItem }
  }
  ${RX_ITEM_FRAGMENT}
`;

export const UPDATE_RX_ITEM_MUTATION = `
  mutation UpdateRxItem($rxItemId: ID!, $input: RxItemUpdateInput!) {
    updateRxItem(rxItemId: $rxItemId, input: $input) { ...RxItem }
  }
  ${RX_ITEM_FRAGMENT}
`;

export const DELETE_RX_ITEM_MUTATION = `
  mutation DeleteRxItem($rxItemId: ID!) {
    deleteRxItem(rxItemId: $rxItemId)
  }
`;

export const ADD_ORDER_MUTATION = `
  mutation AddOrder($encounterId: ID!, $input: OrderInput!) {
    addOrder(encounterId: $encounterId, input: $input) { ...Order }
  }
  ${ORDER_FRAGMENT}
`;

export const UPDATE_ORDER_MUTATION = `
  mutation UpdateOrder($orderId: ID!, $input: OrderUpdateInput!) {
    updateOrder(orderId: $orderId, input: $input) { ...Order }
  }
  ${ORDER_FRAGMENT}
`;

/** The simulated result the student enters → status `resulted`. */
export const RECORD_ORDER_RESULT_MUTATION = `
  mutation RecordOrderResult($orderId: ID!, $input: OrderResultInput!) {
    recordOrderResult(orderId: $orderId, input: $input) { ...Order }
  }
  ${ORDER_FRAGMENT}
`;

/** Sets `cancelled` (not a delete). */
export const CANCEL_ORDER_MUTATION = `
  mutation CancelOrder($orderId: ID!) {
    cancelOrder(orderId: $orderId) { ...Order }
  }
  ${ORDER_FRAGMENT}
`;

export const DELETE_ORDER_MUTATION = `
  mutation DeleteOrder($orderId: ID!) {
    deleteOrder(orderId: $orderId)
  }
`;

// ── Mutations: the chat plumbing ───────────────────────────────────────────

export const CREATE_CHAT_THREAD_MUTATION = `
  mutation CreateCaseChatThread($caseId: ID!, $title: String, $encounterId: ID) {
    createCaseChatThread(caseId: $caseId, title: $title, encounterId: $encounterId) {
      ...ChatThreadRow
    }
  }
  ${CHAT_THREAD_ROW_FRAGMENT}
`;

/** An empty title restores the auto title. */
export const RENAME_CHAT_THREAD_MUTATION = `
  mutation RenameCaseChatThread($threadId: ID!, $title: String) {
    renameCaseChatThread(threadId: $threadId, title: $title) { ...ChatThreadRow }
  }
  ${CHAT_THREAD_ROW_FRAGMENT}
`;

export const DELETE_CHAT_THREAD_MUTATION = `
  mutation DeleteCaseChatThread($threadId: ID!) {
    deleteCaseChatThread(threadId: $threadId)
  }
`;

// ── Mutations: the AI. Each of these spends پارسه. ──────────────────────────
//
// They are MUTATIONS even where they look like reads: they persist two messages
// into a thread and they cost money. A money-spending Query is a trap for any
// client with a normalized cache. Every one returns the thread with ONLY the two
// new messages, so the screen appends rather than re-reads.

/** Patient-level chat. `threadId` omitted = open a new thread; the answer carries it. */
export const ASK_ABOUT_CASE_MUTATION = `
  mutation AskAboutCase($caseId: ID!, $threadId: ID, $question: String!) {
    askAboutCase(caseId: $caseId, threadId: $threadId, question: $question) {
      answer
      confidence
      relevantReferences
      followUpSuggestions
      thread { ...ChatThreadFull }
    }
  }
  ${CHAT_THREAD_ROW_FRAGMENT}
  ${CHAT_MESSAGE_FRAGMENT}
  ${CHAT_THREAD_FULL_FRAGMENT}
`;

/** Encounter-scoped chat on the visit's copilot thread (created on first use). */
export const ASK_IN_ENCOUNTER_MUTATION = `
  mutation AskInEncounter($encounterId: ID!, $question: String!) {
    askInEncounter(encounterId: $encounterId, question: $question) {
      answer
      confidence
      relevantReferences
      followUpSuggestions
      thread { ...ChatThreadFull }
    }
  }
  ${CHAT_THREAD_ROW_FRAGMENT}
  ${CHAT_MESSAGE_FRAGMENT}
  ${CHAT_THREAD_FULL_FRAGMENT}
`;

/** `narrative` is ALWAYS populated and leads; `items` is a best-effort parse. */
export const ENCOUNTER_DIFFERENTIAL_MUTATION = `
  mutation EncounterDifferential($encounterId: ID!) {
    encounterDifferential(encounterId: $encounterId) {
      narrative
      items {
        condition
        likelihood
        reasoning
        discriminators
        nextSteps
      }
      thread { ...ChatThreadFull }
    }
  }
  ${CHAT_THREAD_ROW_FRAGMENT}
  ${CHAT_MESSAGE_FRAGMENT}
  ${CHAT_THREAD_FULL_FRAGMENT}
`;

/** A DRAFT of the four SOAP fields — never auto-applied. */
export const SUGGEST_ENCOUNTER_SOAP_MUTATION = `
  mutation SuggestEncounterSoap($encounterId: ID!) {
    suggestEncounterSoap(encounterId: $encounterId) {
      narrative
      s
      o
      a
      p
      thread { ...ChatThreadFull }
    }
  }
  ${CHAT_THREAD_ROW_FRAGMENT}
  ${CHAT_MESSAGE_FRAGMENT}
  ${CHAT_THREAD_FULL_FRAGMENT}
`;

export const SUGGEST_ENCOUNTER_ORDERS_MUTATION = `
  mutation SuggestEncounterOrders($encounterId: ID!) {
    suggestEncounterOrders(encounterId: $encounterId) {
      narrative
      items {
        orderType
        title
        reason
        urgency
        code
        category
      }
      thread { ...ChatThreadFull }
    }
  }
  ${CHAT_THREAD_ROW_FRAGMENT}
  ${CHAT_MESSAGE_FRAGMENT}
  ${CHAT_THREAD_FULL_FRAGMENT}
`;

/** Over the rx items WITH a formulary slug; `skippedDrugNames` lists the rest. Warnings verbatim. */
export const CHECK_ENCOUNTER_INTERACTIONS_MUTATION = `
  mutation CheckEncounterInteractions($encounterId: ID!) {
    checkEncounterInteractions(encounterId: $encounterId) {
      hasInteractions
      warnings {
        severity
        description
        drugPair
        source
        warningType
        drug
        condition
      }
      skippedDrugNames
      thread { ...ChatThreadFull }
    }
  }
  ${CHAT_THREAD_ROW_FRAGMENT}
  ${CHAT_MESSAGE_FRAGMENT}
  ${CHAT_THREAD_FULL_FRAGMENT}
`;

/** A TRANSCRIPT, never audio. Writes NOTHING — a draft the student confirms. */
export const PARSE_CASE_VOICE_MUTATION = `
  mutation ParseCaseVoice($caseId: ID!, $transcript: String!) {
    parseCaseVoice(caseId: $caseId, transcript: $transcript) {
      displayName
      ageYears
      ageDisplay
      sex
      chiefComplaint
      entries {
        section
        body
        structured
      }
      transcript
    }
  }
`;

// ── Mutations: the money ───────────────────────────────────────────────────

/** `amount` is in THE GATEWAY'S wire unit — src/constants/gateway.js owns the conversion. */
export const TOP_UP_WALLET_MUTATION = `
  mutation TopUpWallet($gateway: String!, $amount: Int!, $description: String, $returnUrl: String) {
    topUpWallet(gateway: $gateway, amount: $amount, description: $description, returnUrl: $returnUrl) {
      gateway
      authority
      paymentUrl
      invoiceId
    }
  }
`;

/** Mark-then-grant, server-side. `balance` is the balance AFTER the grant. */
export const REDEEM_GIFT_CODE_MUTATION = `
  mutation RedeemGiftCode($code: String!) {
    redeemGiftCode(code: $code) {
      giftCode { ...GiftCode }
      balance { ...WalletBalance }
      granted
    }
  }
  ${GIFT_CODE_FRAGMENT}
  ${WALLET_BALANCE_FRAGMENT}
`;
